<template>
  <v-container fluid class="bg-white">
    <v-card class="mx-4" elevation="2">
      <v-card-title class="justify-center font-weight-bold text-muted"
        >Buscador de clientes</v-card-title
      >
      <v-card-text>
        <channel-selector
          :default-channel="channelSelected"
          :loading="customers.loading"
          :with-all-channels-option="viewAllChannels"
          :channels="allowedChannels"
          @channelSelected="channelHasChanged"
        />
        <div class="d-flex justify-center my-3">
          <search-input
            v-model="search"
            :loading="customers.loading"
            :disabled="customers.loading"
            placeholder="Buscar N°, código o Nombre del cliente."
            class="w-50"
            @onSearch="searchCustomers"
          />
        </div>
        <p class="text-left">Buscados recientemente</p>

        <last-seen-card
          :items="lastSeen.data"
          :disabled="lastSeen.loading"
          @showLastSeen="getCustomerInfo"
        />
      </v-card-text>
    </v-card>
    <v-card class="ma-4 pa-4" elevation="2">
      <customers-list
        class="mx-4"
        :items="customers.data"
        :loading="customers.loading"
        @onCustomerClick="getCustomerInfo"
        @onCustomerContactsClick="getCustomerContacts"
        @onTableChange="loadCustomers"
      />
      <alert-prominent
        v-if="customers.error"
        class="ma-3"
        message="Por favor intente nuevamente."
        title="Ocurrió un error al cargar los clientes."
        :with-action="true"
        max-clicks="3"
        @click="loadCustomers"
      />
      <customer-info
        v-model="customerInfo.modal"
        :loading="customerInfo.loading"
        :data="customerInfo.data"
        :active-username="activeUsername?.username"
        @setLastSeen="setLastSeen"
      />
      <customer-contacts
        v-model="customerContacts.modal"
        :loading="customerContacts.loading"
        :data="customerContacts.data"
        :active-username="activeUsername?.username"
        :customer-selected="customerContacts.customerInfo"
        @setLastSeen="setLastSeen"
      />
    </v-card>
  </v-container>
</template>
<script>
  import { mapGetters } from 'vuex';
  import axios from '@/plugins/axios';
  import activeUsernameMixin from '@/mixins/activeUsernameMixin';
  import { Channels } from '@/core/constants/channelConstants.js';
  import { Roles } from '@/core/constants/roleConstants.js';

  import ChannelSelector from '@/components/buttons/ChannelSelector.vue';
  import SearchInput from '@/components/SearchInput.vue';
  import LastSeenCard from './components/LastSeenCard.vue';
  import CustomersList from './components/CustomersList.vue';
  import CustomerInfo from './modals/CustomerInfo.vue';
  import CustomerContacts from './modals/CustomerContacts.vue';
  import AlertProminent from '@/components/AlertProminent.vue';

  export default {
    name: 'CustomerExplorer',
    components: {
      ChannelSelector,
      SearchInput,
      LastSeenCard,
      CustomersList,
      CustomerInfo,
      CustomerContacts,
      AlertProminent,
    },
    mixins: [activeUsernameMixin],
    data() {
      return {
        customers: {
          loading: false,
          data: {
            data: [],
            meta: {
              current_page: 1,
              per_page: 15,
              total: 0,
              last_page: 1,
            },
          },
          error: null,
        },
        customerInfo: {
          modal: false,
          loading: false,
          data: {},
          error: null,
        },
        channelSelected: '',
        search: '',
        viewAllChannels: false,
        allowedChannels: [],
        lastSeen: {
          loading: false,
          data: [],
          error: null,
        },
        customerContacts: {
          modal: false,
          loading: false,
          data: [],
          customerInfo: [],
        },
      };
    },
    computed: {
      ...mapGetters('auth', ['user']),
    },
    created() {
      this.setAllowedChannels();
      this.getLastSeen();
    },
    methods: {
      setAllowedChannels() {
        switch (this.activeUsername?.role) {
          case Roles.MANAGER:
          case Roles.SUB_MANAGER:
          case Roles.SELLER:
            this.viewAllChannels = false;
            this.allowedChannels = [this.user.VTWEG];
            break;
          default:
            this.viewAllChannels = true;
            this.allowedChannels = [Channels.WHOLESALER, Channels.RETAIL];
            break;
        }
        this.channelSelected = this.user.VTWEG;
      },
      async getCustomerContacts(customerInfo) {
        this.customerContacts.loading = true;
        this.customerContacts.modal = true;
        this.customerContacts.customerInfo = customerInfo;
        try {
          const response = await axios.get(
            `v1/customer/contacts`,
            {
              params: {
                customer_id: customerInfo.id,
                channel: this.channelSelected,
                user: this.activeUsername?.username,
              },
            },
            {
              cachable: true,
            }
          );
          this.customerContacts.data = response.data.data;
        } catch (error) {
          this.$swal({
            icon: 'error',
            title: 'Error al cargar los contactos del cliente',
            text: 'Por favor intente nuevamente.',
          });
          this.customerContacts.data = null;
          this.customerContacts.modal = false;
        } finally {
          this.customerContacts.loading = false;
        }
      },
      async getCustomerInfo(customerId) {
        this.customerInfo.modal = true;
        this.customerInfo.loading = true;
        try {
          const response = await axios.get(
            `v1/customer/info`,
            {
              params: {
                customer_id: customerId,
                channel: this.channelSelected,
                user: this.activeUsername?.username,
              },
            },
            {
              cachable: true,
            }
          );
          this.customerInfo.data = response.data.data;
          this.customerInfo.error = null;
        } catch (error) {
          this.customerInfo.error = error;
          this.customerInfo.data = null;
          this.customerInfo.modal = false;
        } finally {
          this.customerInfo.loading = false;
        }
      },
      async loadCustomers(page = 1, perPage = 15) {
        this.customers.loading = true;
        this.customers.data.data = [];
        try {
          const response = await axios.get('v1/customers', {
            params: {
              channel: this.channelSelected,
              user: this.activeUsername?.username,
              search: this.search,
              perPage,
              page,
            },
          });
          this.customers.data = response.data;
          this.customers.error = null;
        } catch (error) {
          this.customers.error = error;
          this.customers.data = {
            data: [],
            meta: {
              current_page: page,
              per_page: perPage,
              total: 0,
              last_page: 1,
            },
          };
        } finally {
          this.customers.loading = false;
        }
      },
      channelHasChanged(channel) {
        this.channelSelected = channel;
        this.loadCustomers();
      },
      searchCustomers() {
        this.loadCustomers();
      },
      async getLastSeen() {
        this.lastSeen.loading = true;
        try {
          const response = await axios.get(
            `v1/customer/receptions/${this.activeUsername?.username}/recently`,
            {
              cachable: true,
            }
          );
          this.lastSeen.data = response.data;
          this.lastSeen.error = null;
        } catch (error) {
          this.lastSeen.error = error;
        }
        this.lastSeen.loading = false;
      },

      async setLastSeen(customerData) {
        this.lastSeen.loading = true;
        try {
          const response = await axios.post(
            `v1/customer/receptions/${this.activeUsername?.username}/recently`,
            customerData,
            {
              cachable: true,
            }
          );
          this.lastSeen.data = response.data;
          this.lastSeen.error = null;
        } catch (error) {
          this.lastSeen.error = error;
        }
        this.lastSeen.loading = false;
      },
    },
  };
</script>
